import type {
  ApiBalanceBySlug, ApiBaseCurrency, ApiChain, ApiCurrencyRates, ApiSwapAsset, ApiSwapRouteType,
} from '../../api/types';
import type { AccountSettings, GlobalState, TradeDirection, UserSwapToken } from '../types';
import { SwapType } from '../../util/swap/types';

import {
  DEFAULT_SWAP_FIRST_TOKEN_SLUG,
  DEFAULT_SWAP_SECOND_TOKEN_SLUG,
  TONCOIN,
} from '../../config';
import { calculateTokenPrice } from '../../util/calculatePrice';
import { toBig } from '../../util/decimals';
import memoize from '../../util/memoize';
import {
  getEffectiveRampCurrencies, getOffRampBaselineCurrencies, getOnRampBaselineCurrencies,
} from '../../util/rampCurrencies';
import { getSwapType } from '../../util/swap/getSwapType';
import { getChainBySlug, getIsNativeToken } from '../../util/tokens';
import withCache from '../../util/withCache';
import {
  selectCurrentAccount,
  selectCurrentAccountId,
  selectCurrentAccountSettings,
  selectCurrentAccountState,
  selectIsHardwareAccount,
} from './accounts';
import { selectAccountTokensMemoizedFor } from './tokens';
import { selectIsOffRampAllowed, selectIsOnRampAllowed } from './transfer';

const EMPTY_CURRENCIES: ApiBaseCurrency[] = [];

function createTokenList(
  swapTokenInfo: GlobalState['swapTokenInfo'],
  tokenInfo: GlobalState['tokenInfo'],
  balancesBySlug: ApiBalanceBySlug,
  baseCurrency: ApiBaseCurrency,
  currencyRates: ApiCurrencyRates,
  sortFn: (tokenA: ApiSwapAsset, tokenB: ApiSwapAsset) => number,
  filterFn?: (token: ApiSwapAsset) => boolean,
): UserSwapToken[] {
  return Object.entries(swapTokenInfo.bySlug)
    .filter(([, token]) => !filterFn || filterFn(token))
    .map(([slug, {
      symbol, name, image,
      decimals, keywords, chain,
      tokenAddress, isPopular, color, priceUsd = 0, label,
    }]): UserSwapToken => {
      const fallbackToken = tokenInfo.bySlug[slug];
      const amount = balancesBySlug[slug] ?? 0n;
      const price = calculateTokenPrice(priceUsd, baseCurrency, currencyRates);
      const totalValue = toBig(amount, decimals).mul(price).toString();

      return {
        symbol,
        slug,
        amount,
        price,
        priceUsd,
        name,
        image: image || fallbackToken?.image,
        decimals,
        isDisabled: false,
        canSwap: true,
        isPopular,
        keywords,
        totalValue,
        color,
        chain,
        tokenAddress,
        label,
      };
    })
    .sort(sortFn);
}

const selectPopularTokensMemoizedFor = withCache((accountId: string) => memoize((
  balancesBySlug: ApiBalanceBySlug,
  tokenInfo: GlobalState['tokenInfo'],
  swapTokenInfo: GlobalState['swapTokenInfo'],
  baseCurrency: ApiBaseCurrency,
  currencyRates: ApiCurrencyRates,
) => {
  const popularTokenOrder = [
    'TON',
    'USD₮',
    'USDT',
    'BTC',
    'ETH',
    'SOL',
    'TRX',
    'XLM',
    'XMR',
    'USDC',
    'LTC',
  ];
  const orderMap = new Map(popularTokenOrder.map((item, index) => [item, index]));

  const filterFn = (token: ApiSwapAsset) => token.isPopular;
  const sortFn = (tokenA: ApiSwapAsset, tokenB: ApiSwapAsset) => {
    const orderIndexA = orderMap.has(tokenA.symbol) ? orderMap.get(tokenA.symbol)! : popularTokenOrder.length;
    const orderIndexB = orderMap.has(tokenB.symbol) ? orderMap.get(tokenB.symbol)! : popularTokenOrder.length;

    return orderIndexA - orderIndexB;
  };
  return createTokenList(swapTokenInfo, tokenInfo, balancesBySlug, baseCurrency, currencyRates, sortFn, filterFn);
}));

const selectSwapTokensMemoizedFor = withCache((accountId: string) => memoize((
  balancesBySlug: ApiBalanceBySlug,
  tokenInfo: GlobalState['tokenInfo'],
  swapTokenInfo: GlobalState['swapTokenInfo'],
  baseCurrency: ApiBaseCurrency,
  currencyRates: ApiCurrencyRates,
) => {
  const sortFn = (tokenA: ApiSwapAsset, tokenB: ApiSwapAsset) => (
    tokenA.name.trim().toLowerCase().localeCompare(tokenB.name.trim().toLowerCase())
  );
  return createTokenList(swapTokenInfo, tokenInfo, balancesBySlug, baseCurrency, currencyRates, sortFn);
}));

const selectAccountTokensForSwapInMemoizedFor = withCache((accountId: string) => memoize((
  balancesBySlug: ApiBalanceBySlug,
  tokenInfo: GlobalState['tokenInfo'],
  swapTokenInfo: GlobalState['swapTokenInfo'],
  accountSettings: AccountSettings,
  baseCurrency: ApiBaseCurrency,
  currencyRates: ApiCurrencyRates,
  areTokensWithNoCostHidden = false,
) => {
  return selectAccountTokensMemoizedFor(accountId)(
    balancesBySlug,
    tokenInfo,
    accountSettings,
    areTokensWithNoCostHidden,
    baseCurrency,
    currencyRates,
  ).filter((token) => token.slug in swapTokenInfo.bySlug && !token.isDisabled);
}));

const selectAccountTokensForSwapOutMemoizedFor = withCache((accountId: string) => memoize((
  balancesBySlug: ApiBalanceBySlug,
  tokenInfo: GlobalState['tokenInfo'],
  swapTokenInfo: GlobalState['swapTokenInfo'],
  accountSettings: AccountSettings,
  baseCurrency: ApiBaseCurrency,
  currencyRates: ApiCurrencyRates,
  areTokensWithNoCostHidden = false,
) => {
  return selectAccountTokensMemoizedFor(accountId)(
    balancesBySlug,
    tokenInfo,
    accountSettings,
    areTokensWithNoCostHidden,
    baseCurrency,
    currencyRates,
  ).filter((token) => (
    token.slug in swapTokenInfo.bySlug
    && (!token.isDisabled || token.slug === TONCOIN.slug)
  ));
}));

export function selectAvailableUserForSwapTokens(global: GlobalState, isSwapOut = false) {
  const balancesBySlug = selectCurrentAccountState(global)?.balances?.bySlug;
  if (!balancesBySlug || !global.tokenInfo || !global.swapTokenInfo) {
    return undefined;
  }

  const accountId = selectCurrentAccountId(global)!;
  const accountSettings = selectCurrentAccountSettings(global) ?? {};
  const { areTokensWithNoCostHidden } = global.settings;

  const selectAccountTokens = isSwapOut
    ? selectAccountTokensForSwapOutMemoizedFor
    : selectAccountTokensForSwapInMemoizedFor;

  return selectAccountTokens(accountId)(
    balancesBySlug,
    global.tokenInfo,
    global.swapTokenInfo,
    accountSettings,
    global.settings.baseCurrency,
    global.currencyRates,
    areTokensWithNoCostHidden,
  );
}

export function selectPopularTokens(global: GlobalState) {
  const balancesBySlug = selectCurrentAccountState(global)?.balances?.bySlug;
  if (!balancesBySlug || !global.swapTokenInfo) {
    return undefined;
  }

  const accountId = selectCurrentAccountId(global)!;

  return selectPopularTokensMemoizedFor(accountId)(
    balancesBySlug,
    global.tokenInfo,
    global.swapTokenInfo,
    global.settings.baseCurrency,
    global.currencyRates,
  );
}

export function selectSwapTokens(global: GlobalState) {
  const balancesBySlug = selectCurrentAccountState(global)?.balances?.bySlug;
  if (!balancesBySlug || !global.swapTokenInfo) {
    return undefined;
  }

  const accountId = selectCurrentAccountId(global)!;

  return selectSwapTokensMemoizedFor(accountId)(
    balancesBySlug,
    global.tokenInfo,
    global.swapTokenInfo,
    global.settings.baseCurrency,
    global.currencyRates,
  );
}

export function selectCurrentSwapTokenIn(global: GlobalState) {
  const { tokenInSlug } = global.currentSwap;
  return tokenInSlug === undefined ? undefined : global.swapTokenInfo.bySlug[tokenInSlug];
}

export function selectCurrentSwapTokenOut(global: GlobalState) {
  const { tokenOutSlug } = global.currentSwap;
  return tokenOutSlug === undefined ? undefined : global.swapTokenInfo.bySlug[tokenOutSlug];
}

export function selectSwapType(global: GlobalState) {
  const {
    tokenInSlug = DEFAULT_SWAP_FIRST_TOKEN_SLUG,
    tokenOutSlug = DEFAULT_SWAP_SECOND_TOKEN_SLUG,
  } = global.currentSwap;
  const { byChain = {} } = selectCurrentAccount(global) ?? {};

  return getSwapType(tokenInSlug, tokenOutSlug, byChain);
}

/** The venue of the last estimate, or the topology default when none has answered yet. */
export function selectSwapRoute(global: GlobalState): ApiSwapRouteType {
  return global.currentSwap.route ?? (selectSwapType(global) === SwapType.OnChain ? 'dex' : 'cex');
}

export function selectIsSwapDisabled(global: GlobalState) {
  return global.restrictions.isSwapDisabled
    || global.settings.isTestnet
    || selectIsHardwareAccount(global);
}

const selectTradeCardCurrenciesMemoized = memoize((
  direction: TradeDirection,
  chain: ApiChain,
  allowedCurrencies?: ApiBaseCurrency[],
) => {
  const baseline = direction === 'buy' ? getOnRampBaselineCurrencies(chain) : getOffRampBaselineCurrencies(chain);

  return getEffectiveRampCurrencies(baseline, allowedCurrencies);
});

/**
 * The fiat currencies the Buy / Sell screen offers next to the counter tokens. Only the native coin of a
 * chain can be bought or sold for card money, so any other screen token gets an empty list.
 */
export function selectTradeCardCurrencies(global: GlobalState): ApiBaseCurrency[] {
  const { tradeDirection, tokenInSlug, tokenOutSlug } = global.currentSwap;
  const screenTokenSlug = tradeDirection === 'buy' ? tokenOutSlug : tokenInSlug;

  if (!tradeDirection || !screenTokenSlug || !getIsNativeToken(screenTokenSlug)) {
    return EMPTY_CURRENCIES;
  }

  const chain = getChainBySlug(screenTokenSlug);
  const isAllowed = tradeDirection === 'buy'
    ? selectIsOnRampAllowed(global, chain)
    : selectIsOffRampAllowed(global, chain);

  if (!isAllowed) {
    return EMPTY_CURRENCIES;
  }

  return selectTradeCardCurrenciesMemoized(tradeDirection, chain, global.restrictions.allowedOnOffRampCurrencies);
}
